import React, { useContext, useEffect, useRef, useState } from "react";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import { Alert, Box, Link, Spinner } from "@amzn/awsui-components-react";
import { useQuery } from "@tanstack/react-query";
import { DashboardApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import DarkModeContext from "../../contexes/DarkModeContext";
import { compressAndSerializeData, decompressAndDeserializeData } from "../../helpers/compressionUtils";
import { Stage } from "../../lusca-search-api/generated-src";
import { PERMISSION_LINK } from "../../constants";



export type DashboardType =
  | "dlq"
  | "testing"
  | "pv_channels"
  | "pv_addon"
  | "pv_tvod";

interface QuickSightDashboardProps {
  stage: Stage;
  dashboardType: DashboardType;
  onLoad?: () => void;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
  setShareHandler: React.Dispatch<React.SetStateAction<(() => Promise<string>) | null>>;
  setFiltersHandler?: React.Dispatch<React.SetStateAction<(() => Promise<string>) | null>>;
  filterRequest?: { filters: string | null; ts: number };
  activeViewName?: string | null;
}

const CONTAINER_HEIGHT = "calc(100vh - 60px)";

export const QuickSightDashboard: React.FC<QuickSightDashboardProps> = ({
  stage,
  dashboardType,
  onLoad,
  searchParams,
  setSearchParams,
  setShareHandler,
  setFiltersHandler,
  filterRequest,
  activeViewName,
}) => {
  const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const dashboardExperienceRefs = useRef<{
    [key: string]: QuickSightEmbedding.DashboardExperience | null;
  }>({});
  const embedAttemptedRefs = useRef<{ [key: string]: boolean }>({});
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const darkModeContext = useContext(DarkModeContext);
  const [isDarkTheme, setIsDarkTheme] = useState(darkModeContext);

  // DLQ supports switching between Prod/Gamma; testing and PV dashboards are single-stage.
  const isMultiStage = dashboardType === "dlq";
  const activeKey = isMultiStage ? stage : dashboardType;

  // Persist a container per key so switching stages doesn't force a re-embed.
  useEffect(() => {
    if (mainContainerRef.current) {
      const containerKeys = isMultiStage ? [Stage.Prod, Stage.Gamma] : [dashboardType];
      containerKeys.forEach((key) => {
        if (!containerRefs.current[key]) {
          const div = document.createElement("div");
          div.style.width = "100%";
          div.style.height = CONTAINER_HEIGHT;
          div.style.position = "absolute";
          div.style.top = "0";
          div.style.left = "0";
          div.style.display = "none";
          containerRefs.current[key] = div;
          mainContainerRef.current!.appendChild(div);
        }
      });
    }
  }, [dashboardType]);

  useEffect(() => {
    Object.keys(containerRefs.current).forEach((key) => {
      const container = containerRefs.current[key];
      if (container) {
        container.style.display = key === activeKey ? "block" : "none";
      }
    });
  }, [activeKey]);

  const dashboardApi = DashboardApiFactory();

  const THEMES = {
    DARK: isMultiStage
      ? stage === Stage.Gamma
        ? "arn:aws:quicksight:us-east-1:239039480348:theme/cloudscape"
        : "arn:aws:quicksight:us-east-1:800989574994:theme/e42b2ecd-7436-45a0-a032-52469c99af64"
      : "arn:aws:quicksight::aws:theme/MIDNIGHT",
    LIGHT: "arn:aws:quicksight::aws:theme/CLASSIC",
  };

  const applyTheme = async (newTheme: boolean) => {
    const currentDashboard = dashboardExperienceRefs.current[activeKey];
    if (currentDashboard) {
      try {
        const setThemePromise = new Promise((resolve, reject) => {
          try {
            const result = currentDashboard.setTheme(
              newTheme ? THEMES.DARK : THEMES.LIGHT
            );
            resolve(result);
          } catch (e) {
            reject(e);
          }
        });

        await Promise.race([
          setThemePromise,
          new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Theme change timed out")),
              30000
            );
          }),
        ]);

        setIsDarkTheme(newTheme);
      } catch (err) {
        console.warn(
          "Theme change timed out (this is expected and harmless - dashboard continues to work):",
          err
        );
      }
    } else {
      console.log("Dashboard not ready, setting initial theme state");
      setIsDarkTheme(newTheme);
    }
  };

  useEffect(() => {
    const currentDashboard = dashboardExperienceRefs.current[activeKey];
    if (currentDashboard) {
      applyTheme(darkModeContext);
    }
  }, [darkModeContext, isLoading]);

  const { data: embedUrlData, error: embedUrlError } = useQuery(
    ["dashboardEmbed", stage, dashboardType],
    async () => {
      const { data } = await dashboardApi.getDashboardEmbedUrl(dashboardType, stage);
      if (!data?.embedUrl) {
        throw new Error(
          (data as { message?: string })?.message ||
            (data as { error?: string })?.error ||
            "Failed to load dashboard"
        );
      }
      return data;
    },
    {
      enabled: !!stage,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    }
  );

  useEffect(() => {
    if (embedUrlError) {
      setError(
        embedUrlError instanceof Error
          ? embedUrlError
          : new Error("Failed to load dashboard")
      );
      setIsLoading(false);
    }
  }, [embedUrlError]);

  const getFilters = async () => {
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");

    const sheetId = await experience.getSelectedSheetId();
    return experience.getFilterGroupsForSheet(sheetId);
  };

  const restoreFilters = async (filtersParams: string) => {
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) throw new Error("Dashboard not loaded");

    const parsedFilterGroups = decompressAndDeserializeData<any[]>(filtersParams);
    const existingFilterGroups = await getFilters();

    // updateFilterGroups will silently ignore the whole request if a request contains
    // any FilterGroupId that doesn't already exist.
    const changedFilterGroups = parsedFilterGroups.filter((newFg) => {
      const existing = existingFilterGroups.find(
        (e) => e.FilterGroupId === newFg.FilterGroupId
      );
      return existing && JSON.stringify(existing) !== JSON.stringify(newFg);
    });
    const addedFilterGroups = parsedFilterGroups.filter(
      (newFg) =>
        !existingFilterGroups.some(
          (e) => e.FilterGroupId === newFg.FilterGroupId
        )
    );

    if (changedFilterGroups.length !== 0) {
      await experience.updateFilterGroups(changedFilterGroups);
    }
    if (addedFilterGroups.length !== 0) {
      await experience.addFilterGroups(addedFilterGroups);
    }
  };

  useEffect(() => {
    if (dashboardExperienceRefs.current[activeKey]) {
      setIsLoading(false);
      return;
    }

    if (
      embedUrlData?.embedUrl &&
      containerRefs.current[activeKey] &&
      !embedAttemptedRefs.current[activeKey]
    ) {
      embedAttemptedRefs.current[activeKey] = true;
      const embedDashboard = async () => {
        try {
          setIsLoading(true);
          const embeddingContext =
            await QuickSightEmbedding.createEmbeddingContext();
          const experience = await embeddingContext.embedDashboard(
            {
              url: embedUrlData.embedUrl,
              container: containerRefs.current[activeKey]!,
              width: "100%",
              height: "100%",
              resizeHeightOnSizeChangedEvent: true,
              onChange: (event: QuickSightEmbedding.EmbeddingEvents) => {
                if (event.eventName === "FRAME_LOADED") {
                  setIsLoading(false);
                  onLoad?.();
                  const filtersParam = searchParams.get("filters");
                  if (filtersParam) {
                    restoreFilters(filtersParam).catch((err) =>
                      console.error("Failed to restore filters:", err)
                    );
                  }
                }
              },
            },
            {
              locale: "en-US",
              sheetOptions: {
                initialSheetId: undefined,
                singleSheet: false,
                emitSizeChangedEventOnSheetChange: true,
              },
              toolbarOptions: {
                export: true,
                undoRedo: true,
                reset: true,
                bookmarks: true,
              },
              themeOptions: {
                themeArn: isDarkTheme ? THEMES.DARK : THEMES.LIGHT,
              },
              attributionOptions: {
                overlayContent: false,
              },
            }
          );
          dashboardExperienceRefs.current[activeKey] = experience;
        } catch (err) {
          console.error("Error embedding dashboard for key:", activeKey, err);
          setError(
            err instanceof Error ? err : new Error("Failed to embed dashboard")
          );
          setIsLoading(false);
        }
      };

      embedDashboard();
    }
  }, [embedUrlData, stage, onLoad, isDarkTheme]);

  const formatQuickSightError = (err: unknown): string => {
    if (err instanceof Error) {
      try {
        const errorData = JSON.parse(
          err.message.replace("QuickSight Error: ", "")
        );
        return errorData.message?.[0] || err.message;
      } catch {
        return err.message.replace("QuickSight Error: ", "");
      }
    }
    return String(err);
  };

  const getAndSetUrlWithFilters = async () => {
    const filters = await getFilters();
    const newParams = new URLSearchParams(searchParams);
    newParams.set("filters", compressAndSerializeData(filters));
    if (activeViewName) {
      newParams.set("view", activeViewName);
    } else {
      newParams.delete("view");
    }
    setSearchParams(newParams);
    return window.location.href;
  };

  const getCompressedFilters = async () => {
    const filters = await getFilters();
    return compressAndSerializeData(filters);
  };

  useEffect(() => {
    setShareHandler(isLoading ? null : () => getAndSetUrlWithFilters);
    setFiltersHandler?.(isLoading ? null : () => getCompressedFilters);
  }, [isLoading, activeViewName, searchParams]);

  useEffect(() => {
    if (!filterRequest || isLoading) return;
    const experience = dashboardExperienceRefs.current[activeKey];
    if (!experience) return;

    if (filterRequest.filters === null) {
      experience.reset().catch((err) =>
        console.error("Failed to reset dashboard:", err)
      );
    } else {
      restoreFilters(filterRequest.filters).catch((err) =>
        console.error("Failed to restore filters:", err)
      );
    }
  }, [filterRequest, isLoading]);

  if (error) {
    const isPermissionError = (error as any)?.response?.status === 403;
    return (
      <Box padding="l">
        <Alert
          type="error"
          header={isPermissionError ? "Permission Error" : "Error loading dashboard"}
          dismissible={false}
        >
          {isPermissionError ? (
            <>
              You don't have permission to view this dashboard. Ask your manager
              to add you to the{" "}
              <Link href={PERMISSION_LINK} external>
                Lusca users group
              </Link>
              .
            </>
          ) : (
            formatQuickSightError(error)
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box padding="n">
      {isLoading && (
        <Box margin="xxl" textAlign="center">
          <Spinner size="large" />
        </Box>
      )}
      <div
        ref={mainContainerRef}
        style={{
          width: "100%",
          height: CONTAINER_HEIGHT,
          position: "relative",
          visibility: isLoading ? "hidden" : "visible",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Box>
  );
};

export default QuickSightDashboard;
